import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withModifiers as _withModifiers, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    "data-testid": "modal-title",
    class: "text-xl font-semibold text-gray-900 dark:text-white"
};
const _hoisted_2 = { class: "space-y-4" };
const _hoisted_3 = { class: "text-sm text-gray-500 dark:text-gray-400" };
const _hoisted_4 = { class: "grid grid-cols-2 gap-6" };
const _hoisted_5 = ["for"];
const _hoisted_6 = { class: "relative" };
const _hoisted_7 = { class: "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" };
const _hoisted_8 = { class: "text-gray-500 dark:text-gray-400 sm:text-sm" };
const _hoisted_9 = ["id", "placeholder", "disabled"];
const _hoisted_10 = {
    key: 0,
    "data-testid": "amount-error",
    class: "mt-2 text-sm text-red-600 dark:text-red-500"
};
const _hoisted_11 = ["for"];
const _hoisted_12 = { class: "relative" };
const _hoisted_13 = { class: "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" };
const _hoisted_14 = { class: "text-gray-500 dark:text-gray-400 sm:text-sm" };
const _hoisted_15 = ["id", "placeholder", "disabled"];
const _hoisted_16 = {
    key: 0,
    class: "mt-2 text-sm text-red-600 dark:text-red-500"
};
const _hoisted_17 = ["for"];
const _hoisted_18 = ["id", "disabled"];
const _hoisted_19 = ["value"];
const _hoisted_20 = ["for"];
const _hoisted_21 = ["id", "min", "placeholder", "disabled"];
const _hoisted_22 = {
    key: 0,
    class: "mt-1 text-xs text-gray-500 dark:text-gray-400"
};
const _hoisted_23 = {
    key: 1,
    class: "mt-2 text-sm text-red-600 dark:text-red-500"
};
const _hoisted_24 = {
    key: 2,
    class: "mt-2 text-sm text-gray-500 dark:text-gray-400"
};
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RecurringPaymentFrequency } from "@/domain/entities/RecurringPaymentRule";
import { useSubscriptionUtils } from "@/shared/composables/useSubscriptionUtils";
import { getCurrencySymbol } from "@/shared/utils/currencyUtils";
import { Modal, Button } from "@eudonet/opal";
export default /*@__PURE__*/ _defineComponent({
    __name: 'UpdateSubscriptionModal',
    props: {
        isOpen: { type: Boolean },
        subscriptionId: {},
        shopperReference: {},
        merchantReference: {},
        currentAmount: {},
        currentTaxAmount: {},
        currentCurrency: {},
        currentFrequency: {},
        currentTransactionCount: {},
        completedTransactionCount: { default: 0 },
        isLoading: { type: Boolean, default: false }
    },
    emits: ["close", "confirm"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const { frequencyOptions } = useSubscriptionUtils();
        const currencySymbol = computed(() => getCurrencySymbol(props.currentCurrency, locale.value));
        const frequency = ref(RecurringPaymentFrequency.Monthly);
        const transactionCount = ref(null);
        const amount = ref(0);
        const taxAmount = ref(0);
        const errors = ref({});
        const isModalOpen = computed({
            get: () => props.isOpen,
            set: () => handleClose(),
        });
        // Watch for modal open to reset form
        watch(() => props.isOpen, (isOpen) => {
            if (isOpen) {
                frequency.value = props.currentFrequency;
                transactionCount.value = props.currentTransactionCount;
                amount.value = props.currentAmount / 100;
                taxAmount.value = props.currentTaxAmount / 100;
                errors.value = {};
            }
        });
        // Computed to handle v-model with proper null conversion for empty input
        const transactionCountModel = computed({
            get: () => transactionCount.value ?? "",
            set: (value) => {
                transactionCount.value = value === "" || value === 0 || value === null ? null : Number(value);
            },
        });
        const validateForm = () => {
            errors.value = {};
            if (amount.value <= 0) {
                errors.value.amount = t("dashboard.subscriptions.modal.update.validation.amountRequired");
            }
            else if (amount.value > 999999) {
                errors.value.amount = t("dashboard.subscriptions.modal.update.validation.amountTooLarge");
            }
            if (taxAmount.value < 0) {
                errors.value.taxAmount = t("dashboard.subscriptions.modal.update.validation.taxAmountNegative");
            }
            if (transactionCount.value === null && props.completedTransactionCount > 0) {
                errors.value.transactionCount = t("dashboard.subscriptions.modal.update.validation.cannotSwitchToUnlimited");
            }
            else if (transactionCount.value !== null && transactionCount.value <= 0) {
                errors.value.transactionCount = t("dashboard.subscriptions.modal.update.validation.transactionCountInvalid");
            }
            else if (transactionCount.value !== null &&
                transactionCount.value <= props.completedTransactionCount) {
                errors.value.transactionCount = t("dashboard.subscriptions.modal.update.validation.transactionCountLessOrEqualCompleted", { completed: props.completedTransactionCount });
            }
            return Object.keys(errors.value).length === 0;
        };
        const canSubmit = computed(() => {
            return (amount.value > 0 &&
                taxAmount.value >= 0 &&
                (transactionCount.value === null || transactionCount.value > 0) &&
                !props.isLoading);
        });
        const hasChanges = computed(() => {
            return (frequency.value !== props.currentFrequency ||
                transactionCount.value !== props.currentTransactionCount ||
                Math.round(amount.value * 100) !== props.currentAmount ||
                Math.round(taxAmount.value * 100) !== props.currentTaxAmount);
        });
        const handleClose = () => {
            if (!props.isLoading) {
                errors.value = {};
                emit("close");
            }
        };
        const handleConfirm = () => {
            if (!validateForm() || !canSubmit.value || !hasChanges.value) {
                return;
            }
            emit("confirm", {
                amount: Math.round(amount.value * 100),
                taxAmount: Math.round(taxAmount.value * 100),
                frequency: frequency.value,
                transactionCount: transactionCount.value,
            });
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: isModalOpen.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((isModalOpen).value = $event)),
                size: "md",
                "data-testid": "update-subscription-modal",
                dismissable: false,
                "close-on-escape": !__props.isLoading,
                onClose: handleClose
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h3", _hoisted_1, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.title")), 1 /* TEXT */)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _unref(t)('dashboard.subscriptions.modal.buttons.cancel'),
                        disabled: __props.isLoading,
                        "data-testid": "cancel-modal-btn",
                        onClick: handleClose
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "success",
                        label: _unref(t)('dashboard.subscriptions.modal.update.confirmButton'),
                        loading: __props.isLoading,
                        disabled: !canSubmit.value || !hasChanges.value,
                        "data-testid": "submit-update-btn",
                        onClick: handleConfirm
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.description", {
                            merchantReference: props.merchantReference,
                        })), 1 /* TEXT */),
                        _createElementVNode("form", {
                            class: "space-y-6",
                            onSubmit: _withModifiers(handleConfirm, ["prevent"])
                        }, [
                            _createElementVNode("div", _hoisted_4, [
                                _createElementVNode("div", null, [
                                    _createElementVNode("label", {
                                        for: `amount-${__props.subscriptionId}`,
                                        class: "block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                    }, [
                                        _createTextVNode(_toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.amountLabel")) + " ", 1 /* TEXT */),
                                        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-red-600 dark:text-red-500" }, "*", -1 /* CACHED */))
                                    ], 8 /* PROPS */, _hoisted_5),
                                    _createElementVNode("div", _hoisted_6, [
                                        _createElementVNode("div", _hoisted_7, [
                                            _createElementVNode("span", _hoisted_8, _toDisplayString(currencySymbol.value), 1 /* TEXT */)
                                        ]),
                                        _withDirectives(_createElementVNode("input", {
                                            id: `amount-${__props.subscriptionId}`,
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((amount).value = $event)),
                                            type: "number",
                                            step: "0.01",
                                            min: "0.01",
                                            "data-testid": "amount-input",
                                            class: _normalizeClass(["block w-full p-2.5 pl-8 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500", { 'border-red-500 focus:border-red-500 focus:ring-red-500': errors.value.amount }]),
                                            placeholder: _unref(t)('dashboard.subscriptions.modal.update.amountPlaceholder'),
                                            disabled: __props.isLoading,
                                            required: ""
                                        }, null, 10 /* CLASS, PROPS */, _hoisted_9), [
                                            [
                                                _vModelText,
                                                amount.value,
                                                void 0,
                                                { number: true }
                                            ]
                                        ])
                                    ]),
                                    (errors.value.amount)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(errors.value.amount), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                _createElementVNode("div", null, [
                                    _createElementVNode("label", {
                                        for: `tax-amount-${__props.subscriptionId}`,
                                        class: "block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                    }, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.taxAmountLabel")), 9 /* TEXT, PROPS */, _hoisted_11),
                                    _createElementVNode("div", _hoisted_12, [
                                        _createElementVNode("div", _hoisted_13, [
                                            _createElementVNode("span", _hoisted_14, _toDisplayString(currencySymbol.value), 1 /* TEXT */)
                                        ]),
                                        _withDirectives(_createElementVNode("input", {
                                            id: `tax-amount-${__props.subscriptionId}`,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((taxAmount).value = $event)),
                                            type: "number",
                                            step: "0.01",
                                            min: "0",
                                            "data-testid": "tax-amount-input",
                                            class: _normalizeClass(["block w-full p-2.5 pl-8 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500", {
                                                    'border-red-500 focus:border-red-500 focus:ring-red-500': errors.value.taxAmount,
                                                }]),
                                            placeholder: _unref(t)('dashboard.subscriptions.modal.update.taxAmountPlaceholder'),
                                            disabled: __props.isLoading
                                        }, null, 10 /* CLASS, PROPS */, _hoisted_15), [
                                            [
                                                _vModelText,
                                                taxAmount.value,
                                                void 0,
                                                { number: true }
                                            ]
                                        ])
                                    ]),
                                    (errors.value.taxAmount)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(errors.value.taxAmount), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ]),
                            _createElementVNode("div", null, [
                                _createElementVNode("label", {
                                    for: `frequency-${__props.subscriptionId}`,
                                    class: "block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                }, [
                                    _createTextVNode(_toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.frequencyLabel")) + " ", 1 /* TEXT */),
                                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-red-600 dark:text-red-500" }, "*", -1 /* CACHED */))
                                ], 8 /* PROPS */, _hoisted_17),
                                _withDirectives(_createElementVNode("select", {
                                    id: `frequency-${__props.subscriptionId}`,
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((frequency).value = $event)),
                                    "data-testid": "frequency-select",
                                    class: "block w-full p-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500",
                                    disabled: __props.isLoading,
                                    required: ""
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(frequencyOptions), (option) => {
                                        return (_openBlock(), _createElementBlock("option", {
                                            key: option.value,
                                            value: option.value
                                        }, _toDisplayString(option.label), 9 /* TEXT, PROPS */, _hoisted_19));
                                    }), 128 /* KEYED_FRAGMENT */))
                                ], 8 /* PROPS */, _hoisted_18), [
                                    [_vModelSelect, frequency.value]
                                ])
                            ]),
                            _createElementVNode("div", null, [
                                _createElementVNode("label", {
                                    for: `transaction-count-${__props.subscriptionId}`,
                                    class: "block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                }, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.transactionCountLabel")), 9 /* TEXT, PROPS */, _hoisted_20),
                                _withDirectives(_createElementVNode("input", {
                                    id: `transaction-count-${__props.subscriptionId}`,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((transactionCountModel).value = $event)),
                                    type: "number",
                                    min: props.completedTransactionCount + 1,
                                    "data-testid": "transaction-count-input",
                                    class: _normalizeClass(["block w-full p-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500", {
                                            'border-red-500 focus:border-red-500 focus:ring-red-500': errors.value.transactionCount,
                                        }]),
                                    placeholder: _unref(t)('dashboard.subscriptions.modal.update.transactionCountPlaceholder'),
                                    disabled: __props.isLoading
                                }, null, 10 /* CLASS, PROPS */, _hoisted_21), [
                                    [_vModelText, transactionCountModel.value]
                                ]),
                                (props.completedTransactionCount > 0)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_22, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.completedTransactionsInfo", {
                                        count: props.completedTransactionCount,
                                    })), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true),
                                (errors.value.transactionCount)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(errors.value.transactionCount), 1 /* TEXT */))
                                    : (_openBlock(), _createElementBlock("p", _hoisted_24, _toDisplayString(_unref(t)("dashboard.subscriptions.modal.update.transactionCountHint")), 1 /* TEXT */))
                            ])
                        ], 32 /* NEED_HYDRATION */)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "close-on-escape"]));
        };
    }
});
